import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { Trash2 } from 'lucide-react';

import {
  type TransitionType,
  type TToolsListItem,
  type UpdateLLMInput,
  stateTransitionSchema,
} from '@intone/shared';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Block types determine what's editable:
// - single_prompt: no header, no transitions
// - double_start: header shown, fixed transition (can edit type/value, can't add/remove)
// - double_end: header shown, no transitions
// - multi: full control (rename, delete, add/remove transitions, etc.)
export type BlockType = 'single_prompt' | 'double_start' | 'double_end' | 'multi'

interface AgentStateBlockProps {
  blockType: BlockType
  stateName: string
  otherStateNames: string[]
  tools: TToolsListItem[]
  // Only used for multi type
  onRename?: (newName: string) => void
  onDelete?: () => void
  onAddTransition?: () => void
  onRemoveTransition?: () => void
  onUpdateTransitionTo?: (to: string) => void
}

const transitionTypeOptions = stateTransitionSchema.options.map(option => {
  const typeValue = option.shape.type.value as TransitionType;
  const labels: Record<TransitionType, string> = {
    number_of_turns: "Number of Turns",
    condition: "Transition Condition",
    tool: "Verification Tool"
  };
  return {
    value: typeValue,
    label: labels[typeValue]
  };
});

export function AgentStateBlock({
  blockType,
  stateName,
  otherStateNames,
  tools,
  onRename,
  onDelete,
  onAddTransition,
  onRemoveTransition,
  onUpdateTransitionTo,
}: AgentStateBlockProps) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<UpdateLLMInput>();
  const fieldPath = `states_config.${stateName}` as const;
  const stateConfig = useWatch({ control, name: fieldPath });

  const getFieldError = (path: string) => {
    const blockErrors = errors.states_config?.[stateName];
    if (!blockErrors) return undefined;

    return path.split('.').reduce((obj: any, key) => obj?.[key], blockErrors);
  };

  if (!stateConfig) return null;

  // Derive permissions from block type
  const showHeader = blockType !== 'single_prompt';
  const showTransitions = blockType === 'double_start' || blockType === 'multi';
  const canModifyTransition = blockType === 'multi';
  const canRename = blockType === 'multi';
  const canDelete = blockType === 'multi';
  const showTransitionTarget = blockType === 'multi';

  return (
    <Card className="border-gray-300">
      {/* Header - only for non-single_prompt */}
      {showHeader && (
        <CardHeader className="bg-gray-50 border-b border-gray-300 py-2 flex-row gap-2 space-y-0">
          <div className="flex-1">
            {
              canRename && onRename ? (
                <Input
                  value={stateName}
                  onChange={(e) => onRename(e.target.value.replace(/\s/g, '_'))}
                  className="h-7 font-semibold border-dashed"
                  placeholder="State name"
                  name={stateName}
                />
              ) : (<span className="text-body font-semibold">{stateName}</span>)
            }
          </div>
          {canDelete && onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="h-7 w-7 p-0 text-destructive hover:text-white hover:bg-destructive hover:border-destructive"
              title="Delete"
            >
              <Trash2 className="w-5 h-5"/>
            </Button>
          )}
        </CardHeader>
      )}

      <CardContent className="p-4 space-y-5">
        {/* Prompt */}
        <Label className="font-medium text-gray-900 grid gap-2">
          Prompt
          <Textarea
            {...register(`${fieldPath}.prompt`)}
            placeholder={`Enter the prompt for ${stateName}`}
            className="min-h-[300px] font-mono resize-y"
          />
          {getFieldError('prompt') && (<span className="text-detail text-destructive">{getFieldError('prompt').message}</span>)}
        </Label>

        {/* Transition Configuration */}
        {showTransitions && otherStateNames.length > 0 && (
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <div className="text-body font-medium text-gray-900">Transition</div>
              {canModifyTransition && !stateConfig.transition && onAddTransition && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onAddTransition}
                  className="h-6"
                >
                  Add Transition
                </Button>
              )}
            </div>

            {stateConfig.transition && (
              <div className="space-y-4 bg-gray-50 p-4 rounded">
                <div className="flex justify-between items-start gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 flex-1">
                    {showTransitionTarget && onUpdateTransitionTo && (
                      <Label className="text-gray-800 space-y-2 block">
                        <span>Target State</span>
                        <Select
                          value={stateConfig.transition.to}
                          onValueChange={onUpdateTransitionTo}
                          name="TargetState"
                        >
                          <SelectTrigger className="h-7">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {otherStateNames.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Label>
                    )}

                    <div className="space-y-1">
                      <Label className="text-gray-800 space-y-2 block">
                        <span>Type</span>
                        <Controller
                          name={`${fieldPath}.transition.type`}
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              value={value}
                              onValueChange={onChange}
                              name="TransitionType"
                            >
                              <SelectTrigger className="h-7" ref={ref}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {transitionTypeOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </Label>
                      <p className="text-detail text-gray-400 leading-tight">
                        {stateConfig.transition.type === 'number_of_turns' &&
                          "The agent will move to the next state after saying this number of phrases. Use for cost saving."}
                        {stateConfig.transition.type === 'condition' &&
                          "The agent will move to the next state when the LLM determines this condition is met."}
                        {stateConfig.transition.type === 'tool' &&
                          "The agent will move to the next state ONLY if the selected verification tool returns \"OK\". Instruct the agent to use that tool in a prompt."}
                      </p>
                    </div>

                    {stateConfig.transition.type === 'number_of_turns' && (
                      <Label className="text-gray-800 space-y-2 block">
                        <span>Turns</span>
                        <Input
                          type="number"
                          min={1}
                          {...register(`${fieldPath}.transition.number_of_turns`, { valueAsNumber: true })}
                          className="h-7"
                        />
                        {getFieldError('transition.number_of_turns') && (<span className="text-detail text-destructive">{getFieldError('transition.number_of_turns').message}</span>)}
                      </Label>
                    )}

                    {stateConfig.transition.type === 'tool' && (
                      <Label className="text-gray-800 space-y-2 block">
                        <span>Tool</span>
                        <Controller
                          name={`${fieldPath}.transition.tool_id`}
                          control={control}
                          render={({field: { value, onChange, ref}}) => (
                            <Select
                              value={value?.toString()}
                              onValueChange={(val) => onChange(parseInt(val))}
                            >
                              <SelectTrigger className="h-7" ref={ref}>
                                <SelectValue placeholder="Select tool" />
                              </SelectTrigger>
                              <SelectContent>
                                {tools.map((tool) => (
                                  <SelectItem key={tool.id} value={tool.id.toString()}>
                                    {tool.name.replace(/\s/g, '_')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {getFieldError('transition.tool_id') && (<span className="text-detail text-destructive">{getFieldError('transition.tool_id').message}</span>)}
                      </Label>
                    )}
                  </div>

                  {canModifyTransition && onRemoveTransition && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onRemoveTransition}
                      className="h-7 w-7 p-0 text-destructive hover:text-white hover:bg-destructive hover:border-destructive"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5"/>
                    </Button>
                  )}
                </div>

                {stateConfig.transition.type === 'condition' && (
                  <Label className="text-gray-800 space-y-2 block">
                    <span>Condition Prompt {`${fieldPath}.transition.condition`}</span>
                    <Textarea
                      {...register(`${fieldPath}.transition.condition`)}
                      placeholder="Enter the condition for this transition"
                      className="min-h-[60px] font-mono"
                    />
                    {getFieldError('transition.condition')  && (<span className="text-detail text-destructive">{getFieldError('transition.condition').message}</span>)}
                  </Label>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tools */}
        <div className="grid gap-2">
          <div className="text-body font-medium text-gray-900">Tools</div>
          <div className="border rounded p-2">
            {tools.length === 0 ? (
              <div className="text-center py-2 text-gray-500 text-detail">No tools available</div>
            ) : (
              <div className="space-y-1">
                {tools.map((tool) => {
                  return (
                    <div key={tool.id} className="flex items-center space-x-2">
                      <Controller
                        control={control}
                        name={`${fieldPath}.tools`}
                        render={({ field: { value: selectedTools = [], onChange } }) => {
                          const isChecked = selectedTools.includes(tool.id);
                          return (
                            <Checkbox
                              id={`${stateName}-tool-${tool.id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                const newTools = checked
                                  ? [...selectedTools, tool.id]
                                  : selectedTools.filter((id) => id !== tool.id);
                                onChange(newTools);
                              }}
                            />
                          );
                        }}
                      />

                      {tool.icon_url && (
                        <img src={tool.icon_url} alt="" className="w-4 h-4 flex-shrink-0" />
                      )}
                      <label
                        htmlFor={`${stateName}-tool-${tool.id}`}
                        className="text-detail leading-none cursor-pointer flex-1"
                      >
                        {tool.name.replace(/\s/g, '_')}
                      </label>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}